import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import Modal from 'tailwind-components/Modal/Modal';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import { safePostCall } from 'helpers/firebaseSetup';
import endpoints from 'constants/endpoints';
import NewsCard from './NewsCard';
import { getFrontendInstance } from 'helpers/getCompanyVariables';
import { workDriveEmbedSrc } from 'helpers/tableSpecialCells/embedding';

const BODY_RESET_STYLE =
  '<style>body,html{margin:0!important;padding:0!important}</style>';

const prepareEmailHTML = str => {
  if (!str) return '';
  // Zoho auto-links URLs inside stored HTML, breaking the structure (e.g. inside DOCTYPE, href attrs).
  // Strip those injected <a> tags back to plain URL text before decoding entities.
  const stripped = str.replace(
    /<a[^>]*href="([^"]*)"[^>]*>[\s\S]*?<\/a>/gi,
    '$1'
  );
  const textarea = document.createElement('textarea');
  textarea.innerHTML = stripped;
  const decoded = textarea.value;
  // Zoho injects <br> tags between block elements as formatting — remove them.
  const noBrBetweenTags = decoded.replace(
    /(?<=>)(\s*<br\s*\/?>\s*)+(?=<)/gi,
    ''
  );
  // Inject reset before </head> if present, otherwise prepend it.
  return noBrBetweenTags.includes('</head>')
    ? noBrBetweenTags.replace('</head>', `${BODY_RESET_STYLE}</head>`)
    : BODY_RESET_STYLE + noBrBetweenTags;
};

function formatNewsCardDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function newsItemDateToMillis(dateStr) {
  if (!dateStr) return null;
  const t = new Date(`${dateStr}T12:00:00`).getTime();
  return Number.isFinite(t) ? t : null;
}

const env = getFrontendInstance();

export default function NewsAndIndustryIntel() {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const cancelRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    cancelRef.current = axios.CancelToken.source();
    safePostCall(
      endpoints.awsProxy.safePost,
      {
        endpoint: endpoints.zoho.getNewsAndIndustryIntel,
        isDemo: env !== 'production',
        isZohoRequest: true
      },
      { cancelToken: cancelRef.current.token }
    )
      .then(res => {
        const raw = JSON.parse(`[${res?.data?.details?.output}]`);
        const items = Array.isArray(raw) ? raw : [];
        setNewsItems(
          [...items].sort((a, b) => {
            const ta = newsItemDateToMillis(a.Date);
            const tb = newsItemDateToMillis(b.Date);
            if (ta == null && tb == null) return 0;
            if (ta == null) return 1;
            if (tb == null) return -1;
            return tb - ta;
          })
        );
      })
      .catch(err => {
        if (!axios.isCancel(err)) console.error('[NewsIntel] error:', err);
      })
      .finally(() => setLoading(false));

    return () => cancelRef.current?.cancel('unmounted');
  }, []);

  const mainContent = (
    <div className='flex h-full flex-col rounded-xl border border-[#d9d9da] bg-white'>
      <div className='show-scrollbar min-h-0 flex-1 overflow-y-auto p-5'>
        {/* The Current: Agent Newsletter */}
        <div data-tour='news-newsletter-section'>
          <div className='mb-5'>
            <h2 className='m-0 text-2xl font-bold text-gray-900'>
              The Current: Agent Newsletter
            </h2>
            <p className='m-0 mt-1 text-base font-medium text-gray-500'>
              Stay current with market news, carrier updates, and agency
              announcements
            </p>
          </div>
          {loading ? (
            <div className='mb-8 flex flex-col items-center justify-center gap-3 py-10'>
              <Loader2
                aria-hidden
                className='size-8 shrink-0 animate-spin text-primary'
              />
              <p className='m-0 text-base font-medium text-gray-800'>
                Loading news...
              </p>
            </div>
          ) : (
            <div className='mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4'>
              {newsItems.map(item => (
                <NewsCard key={item.id} item={item} onClick={setSelectedItem} />
              ))}
            </div>
          )}
        </div>

        {/* Industry Intel */}
        <div>
          <div data-tour='news-intel-section' className='mb-4'>
            <h2 className='m-0 text-2xl font-bold text-gray-900'>
              Industry Intel
            </h2>
            <p className='m-0 mt-1 text-base font-medium text-gray-500'>
              Compliance, CMS documents, etc
            </p>
          </div>
          <iframe
            title='Industry Intel'
            className='min-h-[400px] w-full rounded-xl border border-[#d9d9da]'
            src={workDriveEmbedSrc(
              'https://workdrive.zohoexternal.com/folder/rv6060b79d334d8bd4e96ace0cab7e633f305?layout=list',
              {
                toolbar: false,
                breadcrumbs: true,
                hidetheme: true,
                appearance: true
              }
            )}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ContentWithSidebar
        mainContent={mainContent}
        noSidebar
        sidebarContent={null}
        desktopHeaderClassName='bg-white rounded-t-xl'
      />

      <Modal
        active={!!selectedItem}
        closeModal={() => setSelectedItem(null)}
        size='large'
        title={(() => {
          const textColor = selectedItem?.Title_Color || '#ffffff';
          const formattedDate = formatNewsCardDate(selectedItem?.Date);
          const showDateInHeader =
            Boolean(formattedDate) && !selectedItem?.Image_URL;

          return (
            <div className='min-w-0 py-0.5 text-left'>
              <p
                className='m-0 mb-1 text-sm font-semibold uppercase tracking-widest sm:text-lg'
                style={{ color: textColor }}
              >
                {selectedItem?.Name}
              </p>
              {showDateInHeader && (
                <p
                  className='m-0 text-lg font-bold'
                  style={{ color: textColor }}
                >
                  {formattedDate}
                </p>
              )}
              {selectedItem?.Priority === 1 && (
                <span
                  className='mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium'
                  style={{
                    color: textColor,
                    backgroundColor: `${textColor}40`
                  }}
                >
                  Latest Issue
                </span>
              )}
            </div>
          );
        })()}
        fullBleedHeader
        cardStyle={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
        headerStyle={
          selectedItem
            ? {
                background: `linear-gradient(135deg, ${
                  selectedItem.Title_Background_Color || '#03a0eb'
                } 0%, ${
                  selectedItem.Title_Background_Color || '#03a0eb'
                }99 100%)`,
                marginBottom: 0
              }
            : undefined
        }
        headerTitleClassName='flex min-w-0 flex-1 justify-start items-start text-left pr-2'
        closeButtonStyle={{ color: selectedItem?.Title_Color || '#ffffff' }}
      >
        {selectedItem && (
          <>
            {selectedItem.Image_URL &&
              (() => {
                const imageDate = formatNewsCardDate(selectedItem.Date);
                return (
                  <div className='relative w-full'>
                    <img
                      src={selectedItem.Image_URL}
                      alt={selectedItem.Name}
                      className='block max-h-[420px] w-full rounded-b-lg object-cover object-top'
                    />
                    {imageDate ? (
                      <time
                        className='pointer-events-none absolute right-1 top-2 z-10 rounded-md px-3 py-1.5 text-base text-white opacity-85'
                        dateTime={selectedItem.Date}
                      >
                        {imageDate}
                      </time>
                    ) : null}
                  </div>
                );
              })()}
            <iframe
              ref={iframeRef}
              srcDoc={prepareEmailHTML(selectedItem.HTML_Content)}
              title={selectedItem.Name}
              className='w-full border-0'
              style={{ height: 0 }}
              onLoad={() => {
                const doc = iframeRef.current?.contentDocument;
                if (doc)
                  iframeRef.current.style.height = `${doc.body.scrollHeight}px`;
              }}
              sandbox='allow-same-origin allow-popups'
            />
          </>
        )}
      </Modal>
    </>
  );
}
